import { useState } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";

import Grid from "@mui/material/Grid2";
import { useQuery } from "@tanstack/react-query";
import { sortProducts } from "../../utilities/service";
import { useAuth } from "../../hooks/AuthContext";
import { useNavigate, useParams } from "react-router";
import ProductItem from "./ProductItem";
import SortBy from "./SortBy";
import { scrollToTop, slugToTitle } from "../../utilities";
import BreadcrumbNavigation from "../../components/common/BreadcrumbNavigation";
import ListingSkeleton from "./ListingSkeleton";
import { CommonSection } from "../../components/common/CommonSection";
import { useLoading } from "../../hooks/LoadingContext";
import useApiLoader from "../../hooks/useApiLoader";
import Filter from "./Filter";
import SwipeableEdgeDrawer from "./SwipeableEdgeDrawer";
import { Funnel } from "@phosphor-icons/react";
import PageWrapper from "../../animations/PageWrapper";
import { motion } from "framer-motion";

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15, // delay between each item
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};
const MotionGrid = motion(Grid);
export default function Listing() {
  let { slug, sortKind, flavour } = useParams();
  const { setIsLoading, setProgress } = useLoading();
  let navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();

  const [favorites, setFavorites] = useState({});
  const [filterValues, setFilterValues] = useState({
    weight: null,
    sortType: sortKind || "priceLtoH",
    flavour: flavour || null,
  });
  const [open, setOpen] = useState(false);

  const toggleDrawer = (newOpen) => {
    setOpen(newOpen);
  };
  const { isLoading, error, data } = useQuery({
    queryKey: [
      "sortProducts",
      slug,
      filterValues.sortType,
      filterValues.weight,
      user?.region_id,
    ],
    queryFn: () => sortProducts(slug, user, filterValues),
    onSettled: () => {
      setIsLoading(false);
      setProgress(100);
    },
  });
  useApiLoader(isLoading);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const title = slugToTitle(slug);
  if (error) return <div>Error loading products</div>;
  return (
    <PageWrapper>
      <CommonSection
        sx={{
          my: 3,
          px: { xs: "20px", sm: "40px", md: "60px", lg: "80px", xl: "100px" },
        }}
      >
        <Grid container spacing={3}>
          {/* Sidebar Filters */}
          {/* <Grid size={{ xs: 12, md: 3 }}>
      {isLoading && filterValues.weight == null ? (
        <Skeleton variant="rectangular" width="100%" height={500} />
      ) : (
        <SidebarFilters
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          data={data?.data?.products}
          isFromFlavourScreen={flavour?.length == 1 ? true : false}
        />
      )}
    </Grid> */}

          {/* Product Grid */}
          <Grid size={{ xs: 12 }}>
            {isLoading ? (
              <ListingSkeleton />
            ) : (
              <>
                <BreadcrumbNavigation items={[{ label: title }]} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      color="text.primary"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "16px", md: "18px", lg: "22px" },
                        color: "var(--neutral-black)",
                        textAlign: "left",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {title}
                    </Typography>
                    {data?.data?.products?.length > 0 && (
                      <Typography
                        color="text.primary"
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: "10px", md: "12px", lg: "14px" },
                          marginLeft: 1,
                          color: "var(--neutral-700)",
                        }}
                      >
                        {`( ${data?.data?.products?.length} items )`}
                      </Typography>
                    )}
                  </Box>
                  {isMobile && (
                    <IconButton
                      color="inherit"
                      onClick={toggleDrawer}
                      size="small"
                      aria-label="arrow_forward"
                      sx={{
                        alignSelf: "center",
                        border: "1px solid var(--primary-red-base)",
                        borderRadius: "8px",
                        padding: "6px 16px",
                        color: "var(--primary-red-base)",
                      }}
                      rel="noopener noreferrer"
                    >
                      <Funnel size={20} color="var(--primary-red-base)" />
                      <Typography
                        color="text.primary"
                        sx={{
                          fontWeight: 500,
                          fontSize: isMobile ? "14px" : "16px",
                          marginLeft: 1,
                          color: "var(--primary-red-base)",
                        }}
                      >
                        Filter
                      </Typography>
                    </IconButton>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    my: { xs: 1, sm: 2, md: 3 },
                  }}
                >
                  {/* <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography
                color="text.primary"
                sx={{
                  fontWeight: 600,
                  fontSize: "24px",
                  color: "var(--neutral-black)",
                  textAlign: "left",
                }}
              >
                {title}
              </Typography>
              {data?.data?.products?.length > 0 && (
                <Typography
                  color="text.primary"
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    marginLeft: 1,
                    color: "var(--neutral-700)",
                  }}
                >
                  {`( ${data?.data?.products?.length} items )`}
                </Typography>
              )}
            </Box> */}
                  {!isMobile && (
                    <>
                      <Filter
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        data={data?.data?.products}
                      />
                      <SortBy
                        setFilterValues={setFilterValues}
                        filterValues={filterValues}
                        data={data?.data?.products}
                      />
                    </>
                  )}
                </Box>
                <MotionGrid
                  container
                  spacing={{ xs: 1.5, sm: 3 }}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {data?.data?.products.map((product, index) => (
                    <MotionGrid
                      size={{ xs: 6, md: 3 }}
                      key={product.id}
                      variants={itemVariants}
                    >
                      <ProductItem
                        item={product}
                        favorites={favorites}
                        index={index}
                        navigate={navigate}
                      />
                    </MotionGrid>
                  ))}
                </MotionGrid>
              </>
            )}
          </Grid>
        </Grid>
      </CommonSection>

      <SwipeableEdgeDrawer
        toggleDrawer={toggleDrawer}
        open={open}
        filterValues={filterValues}
        setFilterValues={setFilterValues}
        data={data?.data?.products}
      />
    </PageWrapper>
  );
}
